import { XMarkIcon } from "@heroicons/react/24/solid";
import { PeakStatus, StatusData } from "../../../types";
import { useEffect } from "react";

type Props = {
    top: number | null;
    left: number | null;
    title?: string;
    titleLabel?: string;
    onSelect: (status: PeakStatus) => void;
    type: "peak" | "slope" | "response";
    show: boolean;
    close: () => void;
    currentStatus?: PeakStatus;
    statuses?: StatusData[];
    size:
        | "sm"
        | "md"
        | "lg"
        | "xl"
        | "compare-primary"
        | "compare-secondary"
        | "xxl";
    showLeft?: boolean;
};

const StatusSelectPopup = ({
    titleLabel,
    title,
    top,
    left,
    onSelect,
    type,
    show,
    close,
    currentStatus,
    statuses,
    size,
    showLeft = false,
}: Props) => {
    const headerText =
        title || (titleLabel ? `${titleLabel} ${type} status` : "");

    const verticalOffset =
        size === "xxl" ? -250 : type === "peak" ? -200 : -220;

    // close popup on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (show && !target.closest(".StatusSelectPopup")) {
                close();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [show, close]);

    if (!statuses || !show || !top || !left) return null;

    return (
        <div
            className={`StatusSelectPopup ${
                type === "peak"
                    ? size === "xxl"
                        ? "min-w-[250px]"
                        : "min-w-[200px]"
                    : size === "xxl"
                    ? "min-w-[285px]"
                    : "min-w-[221px]"
            } absolute z-10 top-0 bg-white p-3 border border-gray-disabled shadow-[0px_4px_4px_0px_#00000040]`}
            style={
                showLeft ? { left: verticalOffset } : { right: verticalOffset }
            }
        >
            <div className="w-full flex justify-between items-center pb-1.5">
                <span>{headerText}</span>
                <button
                    onClick={close}
                    className="hover:bg-gray-light p-1 rounded-full"
                >
                    <XMarkIcon
                        className={size === "xxl" ? "size-5" : "size-4"}
                    />
                </button>
            </div>
            {statuses.map(({ status, status_color, status_label }) => (
                <button
                    className={`w-full 
                        ${size === "xxl" ? "text-lg" : "text-[13px]"} 
                        ${
                            status === currentStatus
                                ? "border-blue-medium"
                                : "border-transparent"
                        }
                        text-blue-medium font-semibold p-1.5 flex gap-2 items-center rounded-sm border hover:border-blue-medium`}
                    key={status}
                    onClick={async () => {
                        await onSelect(status);
                        close();
                    }}
                >
                    <div
                        className={`rounded-full ${
                            size === "xxl" ? "h-3 w-3" : "h-2.5 w-2.5"
                        } shrink-0`}
                        style={{
                            backgroundColor: status_color,
                        }}
                    />
                    <span>
                        {status_label || (
                            <>
                                {status}{" "}
                                <span className="capitalize">{type}</span>
                            </>
                        )}
                    </span>
                </button>
            ))}
        </div>
    );
};

export default StatusSelectPopup;
